// The arcade around the machine (docs/06 §2, docs/07 §1.3): a wall, a rain-streaked window, the
// 3:33 clock and the sibling cabinets under tarps. In Act III the tarps have fallen (hollow Maws),
// the payphone hangs by the machine and the missing-person flyer can be read.
import { Container, Graphics, Sprite } from 'pixi.js';
import { PALETTE } from '../../art/palette';
import { getAtlas } from '../../art/rasterize';
import { LEDGER_PAGES } from '../../content/registry';
import type { GameState } from '../../sim/types';
import { setUI } from '../../state/store';
import { LOGICAL_H, LOGICAL_W } from '../app';
import { clock } from '../timeline/clock';

const FLOOR_Y = 300;

export class Arcade extends Container {
  private wall = new Graphics();
  private window = new Sprite();
  private clockFace = new Sprite();
  private siblings: Sprite[] = [];
  private tarps: Sprite[] = [];
  private phone = new Sprite();
  private flyer = new Sprite();
  private act3 = false;

  constructor() {
    super();
    const a = getAtlas();
    const g = this.wall;
    g.rect(0, 0, LOGICAL_W, LOGICAL_H).fill(PALETTE.night);
    // faded wallpaper stripes and a neon trim
    for (let x = 0; x < LOGICAL_W; x += 16) g.rect(x, 16, 2, FLOOR_Y - 16).fill(PALETTE.aubergine);
    g.rect(0, 40, LOGICAL_W, 1).fill(PALETTE.crimson);
    // carpet: a dark check
    g.rect(0, FLOOR_Y, LOGICAL_W, LOGICAL_H - FLOOR_Y).fill(PALETTE.ink);
    for (let y = FLOOR_Y; y < LOGICAL_H; y += 8) for (let x = (y / 8) % 2 ? 0 : 8; x < LOGICAL_W; x += 16) g.rect(x, y, 8, 8).fill(PALETTE.aubergine);
    g.rect(0, FLOOR_Y, LOGICAL_W, 2).fill(PALETTE.dusk);
    this.window.texture = a.tex('window_rain');
    this.window.position.set(130, 52);
    this.clockFace.texture = a.tex('clock');
    this.clockFace.position.set(478, 18);
    for (const x of [110, 466]) {
      const s = new Sprite(a.tex('cabinet_hollow'));
      s.position.set(x, FLOOR_Y - 92);
      this.siblings.push(s);
      const tarp = new Sprite(a.tex('cabinet_tarp'));
      tarp.position.set(x, FLOOR_Y - 92);
      this.tarps.push(tarp);
    }
    this.phone.texture = a.tex('payphone');
    this.phone.position.set(476, 150);
    this.flyer.texture = a.tex('flyer');
    this.flyer.position.set(140, 128);
    this.flyer.eventMode = 'static';
    this.flyer.cursor = 'pointer';
    this.flyer.on('pointertap', () => setUI({ flyer: true }));
    this.addChild(this.wall, this.window, this.clockFace, ...this.siblings, ...this.tarps, this.phone, this.flyer);
  }

  /** Act III: the tarps have fallen, the payphone is on the wall, the flyer can be read. */
  sync(game: GameState, eyes: boolean): void {
    this.act3 = eyes;
    for (const t of this.tarps) {
      t.visible = !eyes;
      t.alpha = 1;
    }
    this.phone.visible = eyes;
    const page = LEDGER_PAGES[0]!.id;
    this.flyer.visible = eyes && !game.meta.lifetime.ledgerPages.includes(page);
    this.flyer.eventMode = this.flyer.visible ? 'static' : 'none';
  }

  /** The Act III reveal: the tarps slide off the sibling cabinets. */
  async dropTarps(): Promise<void> {
    await Promise.all(
      this.tarps.map((t, i) => {
        t.visible = true;
        const y = t.y;
        return clock.tween(t, 700 + i * 200, { y: y + 90, alpha: 0, ease: 'power2.in' }).then(() => {
          t.visible = !this.act3;
          t.y = y;
          t.alpha = 1;
        });
      }),
    );
  }
}
